import GET_AUDIT_CHECK_DOCUMENTS from "@/graphql/audit/GetAuditCheckDocuments.gql";
import UPSERT_AUDIT_CHECK_RESULT__CONNECT_EXISTING_DOCUMENT from "@/graphql/audit/UpsertAuditCheckResultConnectExistingDocument.gql";

import { AuditCheckDocumentKey } from "@/views/document/enum/AuditCheckDocumentKey.enum";
import { AuditCheckDocumentResult } from "@/types/auditCheck.types";

import { GraphQlClient } from "@/types/graphqlClient.types";

/**
 * Get audit check documents.
 * No local cache checking by default.
 * @param apollo Apollo client instance. Can be ApolloClient or DollarApollo.
 * @param companyId Company id
 * @param accountingPeriodId Accounting period id. Optional
 */
export async function getAuditCheckDocuments(
    apollo: GraphQlClient,
    companyId: string,
    accountingPeriodId?: string,
): Promise<AuditCheckDocumentResult[]> {
    if (!apollo) {
        throw new Error("Apollo client is required");
    }

    if (!companyId) {
        throw new Error("companyId is required");
    }

    if ("provider" in apollo) {
        apollo = apollo.provider.defaultClient;
    }

    const response = await apollo.query({
        query: GET_AUDIT_CHECK_DOCUMENTS,
        fetchPolicy: "network-only",
        variables: {
            companyId,
            accountingPeriodId,
        },
    });

    return response.data?.getAuditCheckDocuments;
}

/**
 * Send a mutation to upsert an audit check result if needed.
 * If needed = if the existing audit check result is not defined or
 * if the status is different from the one passed in parameter.
 * @param apollo Apollo client instance. Can be ApolloClient or DollarApollo.
 * @param existingAuditCheckResult Can be undefined or null.
 * @param auditCheckResultKey Concatenation of key / ids
 * @param documentId Document id
 * @param status Status of the audit check result.
 * @returns 1 if the audit check result has been upserted, 0 otherwise.
 */
export async function upsertAuditCheckResultIfNeeded(
    apollo: GraphQlClient,
    existingAuditCheckResult: any,
    auditCheckResultKey: string,
    auditCheckDocumentKey: AuditCheckDocumentKey,
    documentId: string,
    companyId: string,
    status: "IGNORE" | "UNKNOWN" | "VALIDATED",
): Promise<number> {
    if (!apollo) {
        throw new Error("Apollo client is required");
    }

    if (!documentId) {
        throw new Error("documentId is required");
    }

    if (!companyId) {
        throw new Error("companyId is required");
    }

    if (!auditCheckResultKey) {
        throw new Error("auditCheckResultKey is required");
    }

    if ("provider" in apollo) {
        apollo = apollo.provider.defaultClient;
    }

    if (!existingAuditCheckResult || existingAuditCheckResult.status !== status) {
        const upsertVariables = {
            id: existingAuditCheckResult?.id ?? "",
            auditCheckKey: auditCheckDocumentKey,
            key: auditCheckResultKey,
            companyId: companyId,
            status: status,
            documentCreateConnection: { connect: { id: documentId } },
            documentUpdateConnection: { connect: { id: documentId } },
            data: {},
        };

        await apollo.mutate({
            mutation: UPSERT_AUDIT_CHECK_RESULT__CONNECT_EXISTING_DOCUMENT,
            variables: upsertVariables || {},
        });

        return 1;
    }

    return 0;
}
